import { Mic, Send } from 'lucide-react';
import React, { useState } from 'react';
import { useAudioRecorder } from '../../hooks/useAudioRecorder';
import './VoiceInput.css';

interface VoiceInputProps {
  onSubmit: (message: string | Blob) => void;
  isLoading?: boolean;
  placeholder?: string;
}

const VoiceInput: React.FC<VoiceInputProps> = ({
  onSubmit,
  isLoading = false,
  placeholder = 'Type your answer or click the mic to speak...',
}) => {
  const {
    isRecording,
    isSupported,
    startRecording,
    stopRecording,
  } = useAudioRecorder();

  const [textInput, setTextInput] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (textInput.trim()) {
      onSubmit(textInput.trim());
      setTextInput('');
    }
  };

  const handleVoiceToggle = async () => {
    if (isRecording) {
      try {
        // Stop recording and get audio blob
        const audioBlob = await stopRecording();
        // Auto-submit the audio blob directly
        onSubmit(audioBlob);
        setTextInput('');
      } catch (error) {
        console.error('Failed to process audio:', error);
        alert('Failed to process audio. Please try again or use text input.');
      }
    } else {
      try {
        await startRecording();
      } catch (error) {
        console.error('Failed to start recording:', error);
        alert('Failed to access microphone. Please check your browser permissions.');
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="voice-input-container">
      <div className="input-wrapper">
        <textarea
          value={textInput}
          onChange={(e) => setTextInput(e.target.value)}
          placeholder={placeholder}
          disabled={isLoading || isRecording}
          className="text-input"
          rows={3}
        />
        
        <div className="input-controls">
          {isSupported && (
            <button
              type="button"
              onClick={handleVoiceToggle}
              className={`voice-button ${isRecording ? 'recording' : ''}`}
              disabled={isLoading}
              title={isRecording ? 'Stop recording' : 'Start voice input'}
            >
              <Mic size={20} />
            </button>
          )}
          
          <button
            type="submit"
            className="submit-button"
            disabled={isLoading || !textInput.trim()}
          >
            {isLoading ? 'Sending...' : (
              <>
                <Send size={16} />
                Send
              </>
            )}
          </button>
        </div>
      </div>

      {!isSupported && (
        <p className="warning-message">
          Voice input is not supported in your browser. Please use the text input.
        </p>
      )}
    </form>
  );
};

export default VoiceInput;
